"use client";

import { useRef } from "react";
import { useInView } from "framer-motion";
import type { IconType } from "react-icons";
import { FaChartLine, FaCode, FaPalette, FaSearch } from "react-icons/fa";
import { FaGaugeHigh } from "react-icons/fa6";
import { DashboardAnimatedCard } from "@/components/sections/dashboard-animated-card";
import { HeroCtaButton } from "@/components/ui/hero-cta-button";
import {
  HERO_CONTENT_ENTRANCE,
  HERO_ENTRANCE_DURATION,
  HERO_ENTRANCE_PERSPECTIVE,
  HERO_ENTRANCE_PERSPECTIVE_ORIGIN,
  HERO_TICKER_ENTRANCE,
  type HeroEntranceConfig,
} from "@/lib/hero-entrance-motion";

const methodSteps = [
  {
    number: "01",
    title: "AUDIT 100% GRATUIT",
    description:
      "On analyse votre site actuel et on identifie précisément où vous perdez des prospects.",
    icon: FaSearch,
  },
  {
    number: "02",
    title: "Design sur-mesure",
    description: "Maquette pensée pour convertir, validée avec vous avant tout développement.",
    icon: FaPalette,
  },
  {
    number: "03",
    title: "Développement",
    description: "On transforme la maquette en site fonctionnel, prêt à accueillir vos clients.",
    icon: FaCode,
  },
  {
    number: "04",
    title: "STRATEGIE + SEO INTÉGRÉ",
    description:
      "On optimise votre site pour qu'il remonte dans les premiers résultats de recherches Google.",
    icon: FaChartLine,
  },
  {
    number: "05",
    title: "Suivi et ajustements",
    description:
      "On reste à vos côtés jusqu'à vos premiers résultats, en mesurant et en corrigeant chaque point de friction.",
    icon: FaGaugeHigh,
  },
] as const satisfies ReadonlyArray<{
  number: string;
  title: string;
  description: string;
  icon: IconType;
}>;

const STEP_ENTRANCE_CONFIGS = [
  { rotateZ: 5, originX: -300, originY: 200, originZ: -280 },
  { rotateZ: 3, originX: -150, originY: 220, originZ: -300 },
  { rotateZ: 0, originX: 0, originY: 200, originZ: -280 },
  { rotateZ: -3, originX: 150, originY: 220, originZ: -300 },
  { rotateZ: -5, originX: 300, originY: 200, originZ: -280 },
] as const satisfies readonly HeroEntranceConfig[];

const STEP_NUMBER_GRADIENT =
  "bg-gradient-to-r from-emerald-600 via-teal-600 to-green-600 bg-clip-text text-transparent";

const STEP_GLASS_SURFACE_CLASS =
  "relative w-full rounded-[1.75rem] bg-white/55 shadow-[0_24px_70px_-28px_rgba(15,23,42,0.28),0_8px_24px_-12px_rgba(15,23,42,0.12)] ring-1 ring-white/80 backdrop-blur-md md:rounded-[2rem]";

function MethodStepColumn({
  number,
  title,
  description,
  icon: Icon,
}: (typeof methodSteps)[number]) {
  return (
    <article className="flex h-full flex-col">
      <div
        className={`${STEP_GLASS_SURFACE_CLASS} flex h-full flex-col overflow-hidden`}
      >
        <div className="relative shrink-0">
          <div className="flex min-h-[6.75rem] items-center justify-center px-4 pt-5 pb-5 sm:min-h-[7.25rem] sm:px-5 sm:pt-6 sm:pb-6">
            <span
              aria-hidden
              className={`select-none font-black leading-none tracking-tight ${STEP_NUMBER_GRADIENT}`}
              style={{
                fontSize: "clamp(3.25rem, 5vw, 4.5rem)",
                filter: "drop-shadow(0 8px 14px rgba(5, 150, 105, 0.14))",
              }}
            >
              {number}
            </span>
          </div>

          <div
            aria-hidden
            className="h-[2px] w-full bg-gradient-to-r from-emerald-600 via-teal-600 to-green-600"
          />
        </div>

        <div className="flex flex-1 flex-col items-center px-4 pb-6 pt-5 text-center sm:px-5 sm:pb-7 sm:pt-6">
          <h3 className="max-w-[15rem] font-sans text-[0.72rem] font-extrabold uppercase tracking-[0.16em] text-[#1C1C1C] sm:text-xs">
            {title}
          </h3>

          <p className="mt-4 max-w-[14.5rem] flex-1 text-[0.8rem] leading-[1.7] text-zinc-500 sm:text-[0.84rem]">
            {description}
          </p>

          <span className="mt-6 text-teal-600 sm:mt-7">
            <Icon className="text-xl sm:text-2xl" aria-hidden />
          </span>
        </div>
      </div>
    </article>
  );
}

export function BenefitsStrip() {
  const sectionRef = useRef<HTMLElement>(null);
  const isSectionInView = useInView(sectionRef, { once: true, amount: 0.15 });

  return (
    <section ref={sectionRef} className="bg-[#f3f3f3] py-14 sm:py-16">
      <div
        className="mx-auto flex max-w-6xl flex-col gap-12 px-6 sm:gap-14"
        style={{
          perspective: HERO_ENTRANCE_PERSPECTIVE,
          perspectiveOrigin: HERO_ENTRANCE_PERSPECTIVE_ORIGIN,
        }}
      >
        <DashboardAnimatedCard
          config={HERO_CONTENT_ENTRANCE}
          active={isSectionInView}
          finalScale={1}
          settleAfterEntrance
          className="mx-auto w-full max-w-3xl text-center"
        >
          <h2 className="mx-auto max-w-4xl text-balance text-[1.5rem] font-bold leading-[1.08] tracking-[-0.04em] text-[#1C1C1C] sm:text-3xl md:text-4xl md:leading-[1.05]">
            Notre méthode en{" "}
            <span className={STEP_NUMBER_GRADIENT}>5 étapes</span>
          </h2>
        </DashboardAnimatedCard>

        <div
          className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-5 lg:grid-cols-5 lg:gap-4 xl:gap-5"
          style={{ transformStyle: "preserve-3d" }}
        >
          {methodSteps.map((step, index) => (
            <DashboardAnimatedCard
              key={step.number}
              config={STEP_ENTRANCE_CONFIGS[index]}
              active={isSectionInView}
              finalScale={1}
              settleAfterEntrance
              className="h-full"
            >
              <MethodStepColumn {...step} />
            </DashboardAnimatedCard>
          ))}
        </div>

        <DashboardAnimatedCard
          config={HERO_TICKER_ENTRANCE}
          active={isSectionInView}
          finalScale={1}
          settleAfterEntrance
          delay={HERO_ENTRANCE_DURATION}
          className="flex justify-center pt-2 sm:pt-4"
        >
          <HeroCtaButton>Voir si mon projet est éligible</HeroCtaButton>
        </DashboardAnimatedCard>
      </div>
    </section>
  );
}
